/**
 * Job-type registry. Definitions register THEMSELVES on import (`defineJob(...)` as a module
 * side-effect, the same idiom as `registerPendingTaskDescBuilder`), so this module imports nothing but
 * types. That is what keeps the dependency direction one-way: store → registry → nothing. A handler is
 * free to import the store and enqueue follow-up work without creating a cycle.
 */

import { logger } from '@adornis/base/logging.js';
import { EXTRA_STEP_TYPES, STEP_JOB_TYPES, type JobDefinition, type JobPayload } from './job.js';

const definitions = new Map<string, JobDefinition<any>>();

/** Registers a job type. Throws on a duplicate: two definitions for one type is always a bug. */
export function defineJob<P extends JobPayload>(definition: JobDefinition<P>): JobDefinition<P> {
  const type = definition.type?.trim();
  if (!type) throw new Error('defineJob: a job definition needs a non-empty type');
  if (definitions.has(type)) throw new Error(`defineJob: job type "${type}" is already defined`);

  definitions.set(type, definition);
  logger.info({ type }, 'Job type registered');
  return definition;
}

export function getJobDefinition<P extends JobPayload = JobPayload>(type: string): JobDefinition<P> | undefined {
  return definitions.get(type) as JobDefinition<P> | undefined;
}

export function listJobDefinitions(): JobDefinition[] {
  return [...definitions.values()];
}

export function isKnownJobType(type: string): boolean {
  return definitions.has(type);
}

/**
 * Checks that the job types of one step agree about what closing it means. Returns the problems it found.
 *
 * THE ONE FAILURE THE DECLARATIVE FORM STILL ALLOWS, and the reason this exists. Moving the rules onto each
 * definition (`onStepComplete`) removed the step name, the wrapper and the map that used to have to agree — but three
 * types sharing one step must now point at the same OBJECT, and nothing in the type system says so. Two of them
 * importing `gradingSchemeStep` while the third declared its own lookalike would close the step differently depending
 * on which row happened to finish last: a different message, a different precondition, a different follow-up, and no
 * error anywhere.
 *
 * Identity, not deep equality — sharing one object is the intent, and two structurally identical literals are exactly
 * the bug this looks for. A step whose types all declare nothing is fine: `task` is that case, because
 * `runStructureStep` writes its own outcome.
 *
 * Logged rather than thrown, from `startJobSystem`. A mismatch means an announcement fires twice or not at all, which
 * is bad and loud in the log; it does not corrupt data the way a missing unique index would, and taking the whole app
 * down at boot over it would be the worse trade.
 */
export function validateStepCompletionWiring(): string[] {
  const problems: string[] = [];

  // BOTH MAPS. `grading` is a step with three producing types and no progress record, so it lives in
  // `EXTRA_STEP_TYPES` — and it needs this check for exactly the reason the solution steps do: `job-hooks.ts` runs
  // the completion declared by the TERMINATING row's type, so one type of a step missing the shared declaration means
  // a wave that ends on that type closes quietly and never announces.
  for (const [step, types] of [...Object.entries(STEP_JOB_TYPES), ...Object.entries(EXTRA_STEP_TYPES)]) {
    const registered = types.filter(type => definitions.has(type));
    if (registered.length < 2) continue;

    const distinct = new Set(registered.map(type => definitions.get(type)?.onStepComplete));
    if (distinct.size === 1) continue;

    const declared = registered.map(
      type => `${type}=${definitions.get(type)?.onStepComplete ? 'own declaration' : 'none'}`,
    );
    problems.push(`step "${step}": ${declared.join(', ')} — every type of a step must share one declaration`);
  }

  for (const problem of problems) logger.error({ problem }, 'Step-completion wiring disagrees within a step');
  return problems;
}
